import API from '../apiCreate'
import { getParams } from '../getParams'
import type {
  FilterLeadParam,
  Lead,
  LeadState,
  RequestCreateLead,
  ResponseInsertLead,
  ResponseLead,
  ResponseLeadHistory,
  ResponseLeads,
  ResponseLeadStatuses,
  ResponseManagers,
} from '../types/crm/crm.types'
import type { EmptyResponse, PaginationParam, ResponseWrapper } from '../types/base.types'
import type { ResponseCrmLeadFunnelsList } from '../types/crm/crmLeadFunnels.types'
import type { CrmApiRequestConfig } from './crmApiRequestConfig'

/** Лиды / менеджеры / история статусов лида: `/v1/crm-lead*`, `/v1/crm-manager`. */
export const crmLeadApi = {
  // После перехода на мультиворонки, этот метод возвращает не список лидов, а список связок лид-воронка-менеджер
  getFunnelLeads: (params: {
    status: LeadState
    filter: FilterLeadParam
    paramsPagination: PaginationParam
  }) =>
    API.get<ResponseCrmLeadFunnelsList>(
      `crm-lead/get-leads/${getParams(params)}`,
    ),

  getLead: (params: { lead_id: number }) =>
    API.get<ResponseLead>(`crm-lead/get-lead/${getParams(params)}`),

  getAllManagers: (params?: { role?: string }) =>
    API.get<ResponseManagers>(
      `crm-manager/get-all-managers${getParams(params)}`,
    ),

  getLeadsByUserId: (params: { user_id: number }) =>
    API.get<ResponseLeads>(
      `crm-lead/get-leads-by-user-id/${getParams(params)}`,
    ),

  // только для админ (и junior-admin конечно же)
  getAllLeadsInExcel: (params: { type: LeadState; filter: FilterLeadParam }) =>
    API.get<BlobPart>(
      `crm-lead-excel/get-all-leads-in-excel/${getParams(params)}`,
      {
        responseType: 'blob',
      },
    ),

  // Изменяет поля type и manager_id у лида
  // (если нету у лида менеджера: если МОП - он берёт только себе лидов, если РОП (админ) - он должен передать manager_id)
  changeLeadsState: (
    params: { type: LeadState; manager_id?: number },
    data: { lead_ids: number[] },
  ) =>
    API.patch<EmptyResponse>(
      `crm-lead/change-leads-state/${getParams(params)}`,
      data,
    ),

  updateLeadFields: (
    params: { lead_id: number; funnel_id?: number },
    data: {
      fields: { [key: string]: string }
      /** При смене PHONE: true — перезаписать identity-поля интеграций из номера. */
      update_integrations?: boolean
    },
  ) =>
    API.patch<EmptyResponse>(
      `crm-lead/update-lead-fields/${getParams(params)}`,
      data,
    ),

  updateLeadFieldUi: (
    params: { lead_id: number },
    data: { field_id: number; is_collapsed: boolean },
  ) =>
    API.post<
      ResponseWrapper<{
        lead_id: number
        field_id: number
        is_collapsed: boolean
      }>
    >(`crm-lead/update-field-ui/${getParams(params)}`, data),

  updateLead: (
    params: { lead_id: number },
    data: Pick<Lead, 'is_duplicate' | 'user_id'>,
  ) =>
    API.post<EmptyResponse>(`crm-lead/update-lead/${getParams(params)}`, data),

  // только для head-of-sales (РОП) или админ
  removeLeads: (params: { type: LeadState }, data: { lead_ids: number[] }) =>
    API.post<EmptyResponse>(`crm-lead/remove-leads/${getParams(params)}`, data),

  generateLeadsTemplate: () =>
    API.get<BlobPart>('crm-lead-excel/generate-leads-template', {
      responseType: 'arraybuffer',
    }),
  insertLeadsExcel: (formData: FormData) =>
    API.post<EmptyResponse>('crm-lead-excel/insert-leads-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      disableGlobalErrorTextTruncate: true,
    } as CrmApiRequestConfig),
  insertLead: (data: RequestCreateLead) =>
    API.post<ResponseInsertLead>('crm-lead/insert-lead', data),

  getAllLeadStatuses: (
    params: { lead_id: number }, // с сортировкой, сначала новые
  ) =>
    API.get<ResponseLeadStatuses>(
      `crm-lead-status-history/get-all-lead-statuses/${getParams(params)}`,
    ),
  getAllLeadStatusesByFunnel: (params: {
    lead_id: number
    funnel_id: number
  }) =>
    API.get<ResponseLeadStatuses>(
      `crm-lead-status-history/get-all-lead-statuses/${getParams(params)}`,
    ),
  insertLeadStatus: (params: {
    status_id: number
    lead_funnel_id: number
  }) =>
    API.post<EmptyResponse>(
      `crm-lead-status-history/insert-lead-status/${getParams(params)}`,
    ),
  deleteLeadStatus: (params: { status_history_id: number }) =>
    API.post<EmptyResponse>(
      `crm-lead-status-history/delete-lead-status${getParams(params)}`,
    ),

  getLeadHistory: (
    params: {
      lead_id: number
      paramsPagination: PaginationParam
      changed_by?: number
      lead_funnel_id?: number
      funnel_id?: number
    }, // с сортировкой, сначала новые
  ) =>
    API.get<ResponseLeadHistory>(
      `crm-lead-history/get-lead-history/${getParams(params)}`,
    ),
  downloadLeadHistoryExcel: (params: { lead_id: number }) =>
    API.get<BlobPart>(
      `crm-lead-history/download-lead-history-excel/${getParams(params)}`,
      {
        responseType: 'blob',
      },
    ),

  deleteStudAccount: (params: { lead_id: number }) =>
    API.post<EmptyResponse>(
      `crm-lead/delete-student-account/${getParams(params)}`,
    ),
  bindingStudAccount: (params: { lead_id: number; user_id: number }) =>
    API.post<EmptyResponse>(
      `crm-lead/binding-student-account/${getParams(params)}`,
    ),

  searchLeadsForSelect: (params: {
    filter: string[]
    paramsPagination: PaginationParam
  }) => API.get(`crm-lead/search-for-select/${getParams(params)}`),
}
